import React, { useEffect, useState } from "react";
import { Box, Heading, Stack, Text, Tooltip } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";

import { getSearchStatisticsMonthly } from "../../api/searchApi";

const MONTH_NAMES_FR = [
  "Jan",
  "Fév",
  "Mar",
  "Avr",
  "Mai",
  "Juin",
  "Juil",
  "Août",
  "Sep",
  "Oct",
  "Nov",
  "Déc",
];

const formatMonthLabel = (monthKey, locale = "fr") => {
  if (!monthKey || !monthKey.includes("-")) return monthKey;
  const [yearStr, monthStr] = monthKey.split("-");
  const monthIdx = parseInt(monthStr, 10) - 1;
  if (locale.startsWith("en")) {
    try {
      const d = new Date(parseInt(yearStr, 10), monthIdx, 1);
      return d.toLocaleString("en-US", { month: "short" });
    } catch {
      return monthKey;
    }
  }
  return MONTH_NAMES_FR[monthIdx] || monthKey;
};

const ProfileSearchMonthlyChart = ({ buId, rangeMonths = 6 }) => {
  const { t, i18n } = useTranslation("portal");
  const [buckets, setBuckets] = useState([]);

  useEffect(() => {
    if (!buId) return undefined;
    let cancelled = false;
    const load = async () => {
      try {
        const data = await getSearchStatisticsMonthly(
          { rangeMonths },
          { buId }
        );
        if (!cancelled) setBuckets(data?.buckets || []);
      } catch (err) {
        console.error("Failed to load monthly search statistics", err);
        if (!cancelled) setBuckets([]);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [buId, rangeMonths]);

  const monthData = buckets.map((b) => ({
    label: formatMonthLabel(b.month, i18n.language || "fr"),
    value: b.count || 0,
  }));
  const maxValue = Math.max(...monthData.map((m) => m.value), 1);
  const maxHeight = 65;

  return (
    <Box mb={4} data-testid="asap-profile-monthly-chart">
      <Heading
        size="sm"
        mb={2}
        color="black"
        sx={{ marginBottom: "8px !important" }}
      >
        {t("asapProfile.statsChart.title")}
      </Heading>
      <Stack
        direction="row"
        align="flex-end"
        spacing={3}
        h="80px"
        pt={3}
        overflow="hidden"
      >
        {monthData.map((m) => {
          const barHeight =
            m.value === 0 ? 2 : Math.max(10, (m.value / maxValue) * maxHeight);
          return (
            <Box key={m.label} textAlign="center" flex="1">
              <Tooltip
                label={t("asapProfile.statsChart.tooltip", { value: m.value })}
                placement="top"
                hasArrow
              >
                <Box
                  bg="teal.500"
                  w="100%"
                  borderRadius="md"
                  h={`${barHeight}px`}
                  transition="height 0.3s"
                />
              </Tooltip>
              <Text fontSize="xs" mt={1} sx={{ marginBottom: "0 !important" }}>
                {m.label}
              </Text>
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
};

export default ProfileSearchMonthlyChart;
